import React from 'react';
import { styles } from '../AuthWrapper.styles';
import type { AuthFormState } from '../../hooks/useAuthForm';

interface AuthShellProps {
  form: AuthFormState;
  children: React.ReactNode;
}

/**
 * Presentational shell for the unauthenticated auth screen: branding header,
 * sign-in / create-account tabs, error/success banners and footer. The active
 * form is passed as children.
 */
const AuthShell: React.FC<AuthShellProps> = ({ form, children }) => {
  const { authMode, setAuthMode, contextualMessage, error, successMessage, clearMessages } = form;

  return (
    <div style={styles.pageContainer}>
      {/* Animated background */}
      <div style={styles.backgroundOverlay} />

      <div style={styles.card}>
        {/* Logo and branding */}
        <div style={styles.header}>
          <div style={styles.logoContainer}>
            <div style={styles.logoGlow} />
            <svg
              width="64"
              height="64"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={styles.logo}
            >
              <circle cx="20" cy="8" r="3" fill="#60a5fa" />
              <circle cx="8" cy="20" r="3" fill="#60a5fa" />
              <circle cx="32" cy="20" r="3" fill="#60a5fa" />
              <circle cx="14" cy="32" r="3" fill="#60a5fa" />
              <circle cx="26" cy="32" r="3" fill="#60a5fa" />
              <line x1="20" y1="8" x2="8" y2="20" stroke="#60a5fa" strokeWidth="1.5" opacity="0.4" />
              <line x1="20" y1="8" x2="32" y2="20" stroke="#60a5fa" strokeWidth="1.5" opacity="0.4" />
              <line x1="8" y1="20" x2="14" y2="32" stroke="#60a5fa" strokeWidth="1.5" opacity="0.4" />
              <line x1="32" y1="20" x2="26" y2="32" stroke="#60a5fa" strokeWidth="1.5" opacity="0.4" />
              <line x1="14" y1="32" x2="26" y2="32" stroke="#60a5fa" strokeWidth="1.5" opacity="0.4" />
            </svg>
          </div>
          <h1 style={styles.title}>RetainIQ</h1>
          <p style={styles.subtitle}>Retain your team's critical knowledge</p>
          <p style={styles.tagline}>Predict &amp; prevent knowledge loss</p>
          {contextualMessage && <div style={styles.contextualMessage}>{contextualMessage}</div>}
        </div>

        {/* Tabs */}
        {authMode !== 'confirm' && authMode !== 'resetRequest' && authMode !== 'resetConfirm' && (
          <div style={styles.tabs}>
            <button
              type="button"
              style={{
                ...styles.tab,
                ...(authMode === 'signin' ? styles.tabActive : {}),
              }}
              onClick={() => {
                setAuthMode('signin');
                clearMessages();
              }}
            >
              Sign In
            </button>
            <button
              type="button"
              style={{
                ...styles.tab,
                ...(authMode === 'signup' ? styles.tabActive : {}),
              }}
              onClick={() => {
                setAuthMode('signup');
                clearMessages();
              }}
            >
              Create Account
            </button>
          </div>
        )}

        {/* Error message */}
        {error && <div style={styles.error}>{error}</div>}

        {/* Success message */}
        {successMessage && <div style={styles.success}>{successMessage}</div>}

        {children}

        {/* Footer */}
        <div style={styles.footer}>© 2026 RetainIQ. Secure authentication powered by AWS Cognito.</div>
      </div>
    </div>
  );
};

export default AuthShell;
